import { isSome, Maybe } from '@crocodille/common';
import { Dialog } from '@headlessui/react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import React, { MouseEvent, useCallback, useRef } from 'react';
import CircleButton from '../circle-button/circle-button';

import styles from './modal.module.scss';
import { useEventListener } from '../../../hooks/use-event-listener';
import HeavyCrossIcon from '@components/icons/heavy-cross-icon';

export interface ModalProps {
  readonly open: boolean;
  onClose(): void;
  readonly showCloseButton?: boolean;
  readonly title: string;
  readonly description?: Maybe<string>;
  readonly initialFocus?: React.MutableRefObject<HTMLElement | null>;
  readonly width?: 'auto' | 'fill';
  readonly height?: 'auto' | 'fill';
  readonly transparent?: boolean;
  readonly className?: Maybe<string>;
  readonly hideTitle?: boolean;
  readonly closeOnOutsideClick?: boolean;
  readonly titleIcon?: React.ReactNode;
  readonly inverseButton?: boolean;
  readonly dialogClassName?: Maybe<string>;
  readonly titleAlign?: boolean;
}

const Modal: React.FC<React.PropsWithChildren<ModalProps>> = ({
  open,
  onClose,
  title,
  description,
  children,
  initialFocus,
  width = 'fill',
  height = 'auto',
  className,
  showCloseButton = false,
  hideTitle = false,
  closeOnOutsideClick = true,
  transparent = false,
  inverseButton = false,
  titleIcon,
  dialogClassName,
  titleAlign,
}) => {
  const { t } = useTranslation();
  const dialogRef = useRef(null);
  const handleClick = useCallback(
    (e: MouseEvent) => {
      if (closeOnOutsideClick && e.target === dialogRef.current) {
        onClose();
      }
    },
    [closeOnOutsideClick, onClose],
  );

  useEventListener('keyup', (e) => {
    if (closeOnOutsideClick && e.key === 'Escape') {
      onClose();
    }
  });

  if (!open) {
    return null;
  }

  return (
    <Dialog
      ref={dialogRef}
      className={cn(
        styles.container,
        {
          [styles.widthAuto]: width === 'auto',
          [styles.widthFill]: width === 'fill',
          [styles.heightAuto]: height === 'auto',
          [styles.heightFill]: height === 'fill',
          [styles.isTransparent]: transparent,
        },
        dialogClassName,
      )}
      open={open}
      onClose={() => {
        if (closeOnOutsideClick) {
          onClose();
        }
      }}
      onClick={handleClick}
      initialFocus={initialFocus}
    >
      <Dialog.Panel className={cn(styles.panel, className)}>
        {showCloseButton ? (
          <CircleButton
            className={styles.closeButton}
            label={t('close')}
            hiddenLabel={true}
            onClick={onClose}
            inverse={inverseButton}
          >
            <HeavyCrossIcon className={styles.closeIcon} />
          </CircleButton>
        ) : null}
        <Dialog.Title
          className={cn(styles.title, {
            [styles.isHidden]: hideTitle,
            [styles.titleIcon]: titleIcon,
            [styles.center]: titleAlign,
          })}
        >
          {titleIcon || null}
          <div>{title}</div>
        </Dialog.Title>
        {isSome(description) ? (
          <Dialog.Description>{description}</Dialog.Description>
        ) : null}

        <div className={styles.content}>{children}</div>
      </Dialog.Panel>
    </Dialog>
  );
};

export default Modal;
